
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "@/firebase";
import md5 from "md5";

/**
 * Ajoute une recette (copie) dans le document `${userId}-recipes`.
 * Utilisé pour "dupliquer" une recette de base dans les recettes de l'utilisateur.
 * Retourne l'id créé.
 */
/**
 * Ajoute une recette (copie) dans le document `${userId}-recipes`.
 * Utilisé pour "dupliquer" une recette de base dans les recettes de l'utilisateur.
 * Retourne l'id créé.
 *
 * Note: The base recipe's document id must be provided as the source identifier.
 * Historically we stored a `baseId` attribute inside the object; that attribute
 * may no longer exist. Prefer passing `baseRecipeId` (the document id) as the
 * third parameter. The function will still accept `baseRecipe.id` if present.
 */
export async function addBaseRecipeToUser(userId, baseRecipe, baseRecipeId = null) {
  if (!userId) throw new Error("userId is required");
  if (!baseRecipe) throw new Error("baseRecipe is required");

  const docRef = doc(db, "Recipes", `${userId}-recipes`);
  try {
    const snap = await getDoc(docRef);
    const existing = snap.exists() ? snap.data() : {};
    const baseId = baseRecipeId || baseRecipe.id || baseRecipe.baseId || null;
    if (!baseId) {
      throw new Error("baseRecipeId (document id) is required to copy the base recipe without generating a new id");
    }
    const titleNormalized = String(baseRecipe.title || "").trim().toLowerCase();

    // If the user already has a recipe with the same document id, consider it a duplicate.
    if (existing && Object.prototype.hasOwnProperty.call(existing, baseId)) {
      return { existed: true, existingId: baseId };
    }

    // Fallback duplicate detection by title : si une entrée avec le même titre
    // existe sous un id différent, on la supprime pour éviter les doublons obsolètes.
    const duplicateByTitle = Object.entries(existing).find(([id, r]) => {
      if (!r || id === String(baseId)) return false;
      const t = String(r.title || "").trim().toLowerCase();
      return t && titleNormalized && t === titleNormalized;
    });

    if (duplicateByTitle) {
      const [oldId] = duplicateByTitle;
      delete existing[oldId];
    }

    // Use the base recipe's document id as the key in the user's recipes.
    const recipeId = String(baseId);

    // Copy the base recipe object but strip any presentation-only keys
    // (like `id`) that may have been attached by the UI. We must not add
    // any new attributes — store the document data only. Also, map any
    // base recipe image fields into the user's recipe `url` field so the
    // UI displays the image consistently.
    const userRecipe = { ...baseRecipe };
    // Map image fields from base recipe into user's `url` (compatibility)
    if (baseRecipe.imageUrl) {
      userRecipe.url = baseRecipe.imageUrl;
    } else if (baseRecipe.url) {
      userRecipe.url = baseRecipe.url;
    }

    // Remove UI-injected id if present (we use the document id as the key)
    if (Object.prototype.hasOwnProperty.call(userRecipe, "id")) {
      delete userRecipe.id;
    }
    // Also remove any lingering baseId property to avoid duplicating metadata
    if (Object.prototype.hasOwnProperty.call(userRecipe, "baseId")) {
      delete userRecipe.baseId;
    }

  // When copying a base recipe into a user's collection, mark it as
  // no longer a base recipe in the user's namespace so UI logic that
  // checks `baseRecipe === true` will treat it as a regular user recipe.
  userRecipe.baseRecipe = false;

    const toInsert = {
      ...existing,
      [recipeId]: userRecipe,
    };

  // Use merge to avoid overwriting concurrent changes to other recipe keys
  await setDoc(docRef, toInsert, { merge: true });
    return { id: recipeId, existed: false };
  } catch (err) {
    console.error("Erreur addBaseRecipeToUser:", err);
    throw err;
  }
}



/**
 * Ajoute une seule recette de base au document "all" et renvoie l'id créé.
 */
export async function addBaseRecipe(recipeData) {
  if (!recipeData) throw new Error("recipeData is required");

  const docRef = doc(db, "BaseRecipes", "all");
  try {
    const snap = await getDoc(docRef);
    const existing = snap.exists() ? snap.data() : {};

    const normalizedTitle = String(recipeData.title || "").trim().toLowerCase();
    if (normalizedTitle) {
      const duplicateEntry = Object.entries(existing).find(([, r]) => {
        const t = String(r?.title || "").trim().toLowerCase();
        return t && t === normalizedTitle;
      });
      if (duplicateEntry) {
        const [existingId] = duplicateEntry;
        return { id: existingId, existed: true };
      }
    }

    const recipeId = md5((recipeData.title || "") + Date.now().toString() + Math.random().toString());

    const updated = {
      ...existing,
      [recipeId]: recipeData,
    };

    await setDoc(docRef, updated, { merge: true });

    return { id: recipeId, existed: false };
  } catch (err) {
    console.error("Erreur addBaseRecipe :", err);
    throw err;
  }
}

/**
 * Récupère la map des recettes de base (document `all`). Renvoie un objet map ou {}.
 */
export async function getBaseRecipes() {
  const docRef = doc(db, "BaseRecipes", "all");
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return {};
    return snap.data() || {};
  } catch (err) {
    console.error("Erreur getBaseRecipes :", err);
    throw err;
  }
}

/**
 * Supprime une recette de base du document `all`.
 */
export async function deleteBaseRecipe(recipeId) {
  if (!recipeId) return;
  const docRef = doc(db, "BaseRecipes", "all");
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return;
    const data = snap.data();
    if (!Object.prototype.hasOwnProperty.call(data, recipeId)) return;
    delete data[recipeId];
    await setDoc(docRef, data);
  } catch (err) {
    console.error("Erreur deleteBaseRecipe :", err);
    throw err;
  }
}

/**
 * Wrapper compatible avec l'appel dans l'UI : addRecipeToBaseRecipes(userId, recipeData)
 * Le userId est conservé pour compatibilité mais n'est pas utilisé pour le stockage
 */
export async function addRecipeToBaseRecipes(userId, recipeData) {
  // userId peut être utile pour l'audit/log, ici on l'ignore et on délègue
  return await addBaseRecipe(recipeData);
}

/**
 * Récupère une recette de base par id.
 * Signature compatible avec les appels existants : getBaseRecipe(userId, recipeId)
 * Si seul un argument est passé, il sera interprété comme recipeId.
 */
export async function getBaseRecipeById(userIdOrRecipeId, maybeRecipeId) {
  const recipeId = maybeRecipeId || userIdOrRecipeId;
  if (!recipeId) return null;

  const docRef = doc(db, "BaseRecipes", "all");
  try {
    const snap = await getDoc(docRef);
    if (!snap.exists()) return null;
    const data = snap.data() || {};
    const recipe = data[recipeId] || null;
    return recipe ? { id: recipeId, ...recipe } : null;
  } catch (err) {
    console.error("Erreur getBaseRecipe :", err);
    throw err;
  }
}

/**
 * Met à jour une recette de base existante dans le document `all`.
 * Signature compatible avec l'appel depuis l'UI : updateBaseRecipe(userId, recipeId, recipeData)
 */
export async function updateBaseRecipe(userId, recipeId, recipeData) {
  if (!recipeId) throw new Error('recipeId is required')
  if (!recipeData) throw new Error('recipeData is required')

  const docRef = doc(db, 'BaseRecipes', 'all')
  try {
    const snap = await getDoc(docRef)
    const existing = snap.exists() ? snap.data() : {}

    const updated = {
      ...existing,
      [recipeId]: recipeData,
    }

    await setDoc(docRef, updated)
    return { id: recipeId }
  } catch (err) {
    console.error('Erreur updateBaseRecipe :', err)
    throw err
  }
}

/**
 * Copie toutes les recettes d'un utilisateur source vers un utilisateur cible.
 * Utile pour migration/test de données.
 */
export async function copyUserRecipes(sourceUserId, targetUserId) {
  if (!sourceUserId || !targetUserId) {
    throw new Error('sourceUserId et targetUserId sont requis')
  }

  const sourceDocRef = doc(db, 'Recipes', `${sourceUserId}-recipes`)
  const targetDocRef = doc(db, 'Recipes', `${targetUserId}-recipes`)

  try {
    const sourceSnap = await getDoc(sourceDocRef)
    if (!sourceSnap.exists()) {
      return { copied: 0 }
    }

    const sourceRecipes = sourceSnap.data() || {}
    const recipeCount = Object.keys(sourceRecipes).length

    if (recipeCount === 0) {
      return { copied: 0 }
    }

    // Récupère les recettes existantes de la cible pour éviter d'écraser
    const targetSnap = await getDoc(targetDocRef)
    const targetRecipes = targetSnap.exists() ? targetSnap.data() : {}

    // Merge: les nouvelles recettes + les existantes
    const merged = {
      ...targetRecipes,
      ...sourceRecipes,
    }

    await setDoc(targetDocRef, merged, { merge: true })
    return { copied: recipeCount }
  } catch (err) {
    console.error('Erreur copyUserRecipes:', err)
    throw err
  }
}
